import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "@/context/NotificationsContext";
import { useLang } from "@/context/LangContext";
import {
  Bell, CheckCircle2, Bike, MapPin, XCircle, PartyPopper,
  ChefHat, Pencil, CheckCheck,
} from "lucide-react";

const ICONS = {
  new_order: { Icon: Bell, color: "#E05D3A", bg: "#FFF1EA" },
  order_accepted: { Icon: CheckCircle2, color: "#E05D3A", bg: "#FFF1EA" },
  order_preparing: { Icon: ChefHat, color: "#E05D3A", bg: "#FFF1EA" },
  order_ready: { Icon: ChefHat, color: "#C28A00", bg: "#FFF7E0" },
  new_available_order: { Icon: Bike, color: "#2563EB", bg: "#E8F0FE" },
  driver_assigned: { Icon: Bike, color: "#2563EB", bg: "#E8F0FE" },
  out_for_delivery: { Icon: Bike, color: "#2563EB", bg: "#E8F0FE" },
  order_on_the_way: { Icon: Bike, color: "#2563EB", bg: "#E8F0FE" },
  driver_nearby: { Icon: MapPin, color: "#E05D3A", bg: "#FFF1EA" },
  order_delivered: { Icon: PartyPopper, color: "#00A85A", bg: "#E6F7EF" },
  order_cancelled: { Icon: XCircle, color: "#DC2626", bg: "#FEECEC" },
  order_updated: { Icon: Pencil, color: "#7C3AED", bg: "#F1EBFB" },
};

function timeAgo(iso, lang) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  const ar = lang === "ar";
  if (s < 60) return ar ? "الآن" : "now";
  const m = Math.floor(s / 60);
  if (m < 60) return ar ? `قبل ${m} د` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return ar ? `قبل ${h} س` : `${h}h ago`;
  const dd = Math.floor(h / 24);
  return ar ? `قبل ${dd} يوم` : `${dd}d ago`;
}

export default function Notifications() {
  const { items, unread, refresh, markRead, markAll } = useNotifications();
  const { lang } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onItemClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.order_id) navigate(`/order/${n.order_id}`);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 pb-24" data-testid="notifications-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl font-black text-[#1A1A1A]">
          {lang === "ar" ? "الإشعارات" : "Notifications"}
        </h1>
        {unread > 0 && (
          <button
            onClick={markAll}
            className="flex items-center gap-1.5 text-sm font-semibold text-[#E05D3A] hover:underline"
            data-testid="mark-all-read-btn"
          >
            <CheckCheck className="w-4 h-4" />
            {lang === "ar" ? "تعليم الكل كمقروء" : "Mark all read"}
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-24 text-[#9A9A9A]" data-testid="notifications-empty">
          <Bell className="w-14 h-14 mx-auto mb-4 opacity-40" />
          <p className="text-lg font-medium text-[#6B6B6B]">
            {lang === "ar" ? "لا توجد إشعارات بعد" : "No notifications yet"}
          </p>
          <p className="text-sm mt-1">
            {lang === "ar" ? "ستظهر هنا تحديثات طلباتك" : "Your order updates will appear here"}
          </p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {items.map((n) => {
            const cfg = ICONS[n.type] || { Icon: Bell, color: "#6B6B6B", bg: "#F0F0F0" };
            const { Icon } = cfg;
            return (
              <button
                key={n.id}
                onClick={() => onItemClick(n)}
                className={`w-full text-start flex items-start gap-3 rounded-2xl p-4 border transition-colors ${
                  n.read ? "bg-white border-[#EEE]" : "bg-[#FFF8F5] border-[#FAD9CC]"
                }`}
                data-testid={`notification-${n.id}`}
              >
                <div
                  className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: cfg.bg }}
                >
                  <Icon className="w-5 h-5" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-bold text-[#1A1A1A] truncate flex-1">{n.title}</p>
                    {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-[#E05D3A] flex-shrink-0" />}
                  </div>
                  <p className="text-sm text-[#6B6B6B] mt-0.5">{n.body}</p>
                  <p className="text-xs text-[#9A9A9A] mt-1.5">{timeAgo(n.created_at, lang)}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
